import { z } from 'zod';

import { SUPPORTED_BRIDGE_IDS } from '@/lib/config/serviceWalletRegistry';

/**
 * Zod schemas for the Hyperliquid speed-trading API
 * (`MoonHub.API/facets/SpeedTradingApiServer.ts`).
 *
 * Schemas are intentionally permissive on extra keys (zod objects strip
 * unknowns) but strict on the fields the frontend reads, so a backend shape
 * drift surfaces as a `ZodError` instead of a silent `undefined`.
 */

// --- trade status enums (mirror SpeedTradingTradeStatus / SpeedTradingTransferStatus) ---

export const tradeStatusSchema = z.enum([
    'Pending',
    'Open',
    'Closed',
    'Liquidated',
    'TakeProfit',
    'StopLoss',
    'Failed',
    // a GTC limit order resting on Hyperliquid: it holds the pair's active slot
    // exactly like Pending/Open, but there is no position behind it yet.
    'Resting',
    // terminal: a resting order cancelled before any fill. Never a close.
    'Cancelled',
]);

export const transferStatusSchema = z.enum([
    'Pending',
    'Bridging',
    'Landed',
    'Settled',
    'Refunded',
    'Failed',
]);

// --- GET /speed/config ---

export const speedConfigSchema = z.object({
    networkId: z.number(),
    testnet: z.boolean(),
    manager: z.string(),
    usdc: z.string(),
    hyperCore: z.object({
        coreWriter: z.string(),
        usdc: z.string(),
        coreDepositWallet: z.string(),
        perpDestinationDex: z.number(),
        usdcSystemAddress: z.string(),
        usdcCoreTokenIndex: z.string(),
        positionPrecompile: z.string(),
        withdrawablePrecompile: z.string(),
    }),
    custodialDepositMode: z.string(),
    builderFee: z.object({
        enabled: z.boolean(),
        receiver: z.string().nullable(),
        decibps: z.number(),
        onchainReceiver: z.string(),
        onchainDecibps: z.string(),
    }),
    relayAppFee: z.object({
        enabled: z.boolean(),
        recipient: z.string().nullable(),
        bps: z.number(),
    }),
    monetizationPath: z.enum(['relayAppFees', 'builderApproval', 'none']),
});

// --- GET /speed/gas (factions speed facet) ---

/**
 * HyperEVM chain-load / congestion telemetry served by factions.backend's
 * `speed` facet (`GET /speed/gas`). Utilizations are `gasUsed / gasLimit`
 * fractions in `0..1`; `current` is the latest block, the `avg*` windows are
 * rolling means that stay `null` until enough blocks have been observed
 * (warm-up).
 *
 * The endpoint is feature-flagged: when disabled it returns `{ enabled: false }`
 * and the frontend must render nothing. Modeled as a discriminated union on
 * `enabled` so both shapes parse and anything else (a legacy backend's 404 body,
 * garbage) rejects with a `ZodError`.
 */
export const speedGasMetricsDisabledSchema = z.object({
    enabled: z.literal(false),
});

export const speedGasMetricsActiveSchema = z.object({
    enabled: z.literal(true),
    networkId: z.number(),
    blockNumber: z.number(),
    gasLimit: z.number(),
    current: z.number(),
    avg1m: z.number().nullable(),
    avg5m: z.number().nullable(),
    avg15m: z.number().nullable(),
    avg30m: z.number().nullable(),
    updatedAt: z.string(),
});

export const speedGasMetricsSchema = z.discriminatedUnion('enabled', [
    speedGasMetricsDisabledSchema,
    speedGasMetricsActiveSchema,
]);

// --- GET /speed/markets ---

export const speedMarketSchema = z.object({
    coin: z.string(),
    index: z.number(),
    szDecimals: z.number(),
    maxLeverage: z.number(),
    markPx: z.string().nullable(),
    oraclePx: z.string().nullable(),
    funding: z.string().nullable(),
    openInterest: z.string().nullable(),
    // 24h context (additive backend fields — optional for rollout safety).
    prevDayPx: z.string().nullable().optional(),
    dayNtlVlm: z.string().nullable().optional(),
    // HIP-3 builder-market fields (additive — optional for rollout safety).
    // `dex` is the canonical builder-DEX namespace (null/absent = main dex);
    // `tradeable:false` rows stay listed but cannot be opened, with
    // `unavailableReason` identifies genuinely disabled/stale markets. Isolated-
    // only markets remain tradeable and are identified by `onlyIsolated`.
    // `requiresHip3Activation` marks markets that
    // need the one-time per-user HIP-3 account activation before first trade.
    dex: z.string().nullable().optional(),
    onlyIsolated: z.boolean().optional(),
    tradeable: z.boolean().optional(),
    unavailableReason: z.string().nullable().optional(),
    requiresHip3Activation: z.boolean().optional(),
});

export const speedMarketsResponseSchema = z.object({
    networkId: z.number(),
    markets: z.array(speedMarketSchema),
});

// --- GET /speed/markets/:coin ---

export const leverageTierSchema = z.object({
    maxNotionalUsd: z.string(),
    maxLeverage: z.number(),
    initialLostP: z.number(),
    liqLostP: z.number(),
});

export const leverageInfoSchema = z.object({
    minLeverage: z.number().nullable(),
    maxLeverage: z.number().nullable(),
    leverageStep: z.number().nullable(),
    leverageList: z.array(z.number()).nullable(),
    tiers: z.array(leverageTierSchema),
});

export const hyperliquidMarketSchema = z.object({
    index: z.number(),
    name: z.string(),
    szDecimals: z.number(),
    maxLeverage: z.number(),
    marginTiers: z
        .array(z.object({ lowerBound: z.string(), maxLeverage: z.number() }))
        .optional(),
    onlyIsolated: z.boolean().optional(),
    markPx: z.string().nullable().optional(),
    oraclePx: z.string().nullable().optional(),
    midPx: z.string().nullable().optional(),
    funding: z.string().nullable().optional(),
    openInterest: z.string().nullable().optional(),
});

export const marketDetailResponseSchema = z.object({
    coin: z.string(),
    leverage: leverageInfoSchema,
    market: hyperliquidMarketSchema,
});

// --- GET /speed/markets/:coin/:leverage ---

export const collateralResponseSchema = z.object({
    coin: z.string(),
    leverage: z.number(),
    minCollateralUsd: z.number(),
    maxLeverage: z.number(),
});

// --- GET /speed/position/:coin ---

export const positionSchema = z.object({
    coin: z.string(),
    szi: z.string(),
    entryPx: z.string().nullable(),
    positionValue: z.string(),
    unrealizedPnl: z.string(),
    returnOnEquity: z.string(),
    liquidationPx: z.string().nullable(),
    marginUsed: z.string(),
    maxLeverage: z.number(),
    leverage: z.object({
        type: z.enum(['isolated', 'cross']),
        value: z.number(),
        rawUsd: z.string().optional(),
    }),
});

export const positionResponseSchema = z.object({
    networkId: z.number(),
    coin: z.string(),
    adapter: z.string().nullable(),
    position: positionSchema.nullable(),
});

// --- GET /speed/positions ---
// Every open position in one read. The per-coin endpoint above fetches the whole
// clearinghouse state and keeps one entry, so a client holding several markets
// was paying a full state read per market to see them all.

export const positionsResponseSchema = z.object({
    networkId: z.number(),
    adapter: z.string().nullable(),
    positions: z.array(
        positionSchema.extend({
            /** the HIP-3 builder dex, or null for the main perp dex. */
            dex: z.string().nullable(),
        }),
    ),
});

// --- POST /speed/quote ---

export const quoteResponseSchema = z.object({
    coin: z.string(),
    sizeAsset: z.number(),
    sizeRaw: z.string(),
    limitPx: z.number(),
    limitPxRaw: z.string(),
    roundedNotionalUsd: z.number(),
    orderValueUsd: z.number(),
    notionalUsd: z.number(),
});

// --- POST /speed/trade/open ---

export const builderApprovalSchema = z.object({
    networkId: z.number(),
    manager: z.string(),
    userAddress: z.string(),
    adapter: z.string(),
    receiver: z.string(),
    decibps: z.number(),
    onchainReceiver: z.string(),
    onchainDecibps: z.string(),
    txHash: z.string(),
});

export const fundingMetaSchema = z.object({
    networkId: z.number(),
    manager: z.string(),
    userAddress: z.string(),
    adapter: z.string(),
    custodialWallet: z.string(),
    usdc: z.string(),
    managerUsdc: z.string(),
    coreDepositWallet: z.string(),
    perpDestinationDex: z.number(),
});

export const tradeOpenResponseSchema = z.object({
    success: z.literal(true),
    txHash: z.string(),
    builderApprovalTxHash: z.string().nullable(),
    builderApproval: builderApprovalSchema.nullable(),
    collateralApprovalTxHash: z.string().nullable(),
    fundTxHash: z.string().nullable(),
    fundingShortfallRaw: z.string(),
    funding: fundingMetaSchema,
    coin: z.string(),
    size: z.number(),
    leverage: z.number(),
});

// --- POST /speed/trade/close ---

export const tradeCloseResponseSchema = z.object({
    success: z.literal(true),
    txHash: z.string(),
    coin: z.string(),
});

// --- POST /speed/trade/limit/{open,cancel,execute} ---

/**
 * A GTC limit entry was placed. `status` says which of the two things happened:
 * `Resting` (the order sits on the book, `txHash` is `hl:<oid>`) or `Open` (the
 * price crossed the book and it filled synchronously, so `entryPrice` is set and
 * the row is an ordinary running position from that moment).
 */
export const limitOpenResponseSchema = z.object({
    success: z.literal(true),
    txHash: z.string(),
    coin: z.string(),
    status: z.enum(['Resting', 'Open']),
    /** the HL-rounded price the order actually rests at, as a decimal string. */
    limitPrice: z.string(),
    size: z.number(),
    leverage: z.number(),
    /** only on `status: 'Open'` — the synchronous fill's entry. */
    entryPrice: z.string().nullish().default(null),
});

/**
 * The resting order was cancelled. `Open` means it filled before (or while) the
 * cancel landed, so nothing was cancelled and the row is now a live position.
 */
export const limitCancelResponseSchema = z.object({
    success: z.literal(true),
    coin: z.string(),
    // nullable: the row's hash can still be a `pending:` placeholder the backend
    // declines to echo. A ZodError here would report a failure AFTER the order
    // was already cancelled, which is the one answer that must never be wrong.
    tradeHash: z.string().nullable(),
    status: z.enum(['Cancelled', 'Open']),
});

/** "Fill now": the resting order is cancelled and re-opened at market. */
export const limitExecuteResponseSchema = z.object({
    success: z.literal(true),
    coin: z.string(),
    status: z.literal('Open'),
    txHash: z.string(),
    /** true when the resting order had already filled, so nothing was re-opened. */
    filledBeforeExecute: z.boolean(),
    size: z.number().nullish().default(null),
    leverage: z.number().nullish().default(null),
});

// --- speed jobs: POST /speed/* actions + GET /speed/job/:id ---

/**
 * A single Relay step item the user (metamask mode) must sign and submit.
 * The `data` block is a raw EVM transaction request executed on the origin chain.
 */
export const relayStepSchema = z.object({
    id: z.string().optional(),
    action: z.string().optional(),
    description: z.string().optional(),
    kind: z.string().optional(),
    items: z.array(
        z.object({
            status: z.string().optional(),
            data: z
                .object({
                    from: z.string().optional(),
                    to: z.string(),
                    data: z.string().optional(),
                    value: z.union([z.string(), z.number()]).optional(),
                    chainId: z.number().optional(),
                    maxFeePerGas: z.union([z.string(), z.number()]).optional(),
                    maxPriorityFeePerGas: z
                        .union([z.string(), z.number()])
                        .optional(),
                    gas: z.union([z.string(), z.number()]).optional(),
                })
                .nullable()
                .optional(),
        }),
    ),
});

/**
 * Which bridge the backend actually routed a transfer over. The backend's
 * bridge registry (`GET /speed/bridges`) decides the order per chain: Relay
 * first, LI.FI (an aggregator over third-party bridges) when Relay cannot
 * serve the route, deBridge last. deBridge costs orders of magnitude more, so
 * the UI names the bridge rather than quietly charging for it.
 */
export const bridgeNameSchema = z.enum(['relay', 'lifi', 'debridge']);

/**
 * What a bridge can carry, decided by who signs the leg: the user's own wallet
 * (`userDeposit`), a service wallet against a custodial allowance
 * (`custodialDeposit`), or a service-wallet hop on HyperEVM (`withdraw`).
 */
export const bridgeLegSchema = z.enum([
    'userDeposit',
    'custodialDeposit',
    'withdraw',
]);

/**
 * The backend's bridge registry (`GET /speed/bridges`): every bridge it knows
 * and, per chain id, the bridges that chain uses in failover order. A leg is
 * carried by the bridges enabled on both of its chains, in the origin chain's
 * order. The testing switch's options come from here, so the app never has to
 * know the list itself.
 */
export const bridgeCatalogSchema = z.object({
    bridges: z.array(
        z.object({
            id: z.number().int().positive(),
            name: bridgeNameSchema,
            label: z.string(),
            legs: z.array(bridgeLegSchema),
        }),
    ),
    chains: z.record(z.string(), z.array(bridgeNameSchema)),
});

/**
 * The fee figures a transfer/job payload reports for the route the backend
 * chose. Every member is optional: a backend that predates the failover sends
 * no `fees` at all, and the UI then falls back to the flat estimate the
 * balance endpoints advertise.
 *
 * `bridgeFeeUsd` is a plain decimal USD string ('2.20'), not base units.
 */
export const transferFeesSchema = z.object({
    bridgeFeeUsd: z.string().optional(),
    bridge: bridgeNameSchema.optional(),
});

/**
 * A DISPLAY pre-quote (`POST /speed/withdraw/quote`, `POST /speed/deposit/quote`).
 *
 * Its whole job is to put the real number in front of the user before they
 * commit: which bridge would carry this amount, what it would cost, what would
 * land, and the floor that applies. Nothing here authorizes anything and
 * nothing here is trusted at execution time, where prepare re-quotes and the
 * fund-authorization challenge states the figure that is actually signed for.
 *
 * `ok` is the only required member, and the only one that decides anything (it
 * gates the button). Every other field is soft: a value this app cannot read
 * becomes null and the surface simply says less, because a display number must
 * never be the reason a withdrawal cannot be started. `reason` is plain user
 * copy the backend wrote for an `ok: false`; it is rendered verbatim.
 */
const softQuoteString = z.string().nullish().catch(null);

export const bridgeQuoteResponseSchema = z.object({
    ok: z.boolean(),
    bridge: bridgeNameSchema.nullish().catch(null),
    /** Decimal USD string ('2.20'). */
    bridgeFeeUsd: softQuoteString,
    /** What the quote expects to land, in 6dp base units like every other amount here. */
    estimatedReceive: softQuoteString,
    /** Decimal USD string: the floor that applies to THIS route. */
    minimumUsd: softQuoteString,
    /** Plain user copy when `ok` is false. */
    reason: softQuoteString,
    /** Machine name of a refusal the client can act on (e.g. bridgeTokenUnsupported). */
    code: softQuoteString,
});

export const depositStartPayloadSchema = z.object({
    transferId: z.number(),
    // null on the hyperevmDirect route, which never talks to Relay.
    requestId: z.string().nullable(),
    route: z.enum([
        'metamask',
        'custodialDirect',
        'serviceWalletHop',
        // HyperEVM-origin deposit: no Relay steps, no requestId — the user signs one ERC20
        // transfer into `destinationAddress` on chain 999.
        'hyperevmDirect',
        // "move to trading balance": USDC already in the custodial wallet on 999 is pushed to
        // HyperCore by the backend saga; no Relay, no user transaction, starts in `bridging`.
        'custodialBalance',
    ]),
    originChainId: z.number(),
    originCurrency: z.string(),
    amount: z.string(),
    destinationChainId: z.number(),
    destinationCurrency: z.string(),
    destinationAddress: z.string(),
    steps: z.array(relayStepSchema).optional(),
    txHashes: z.array(z.string()).optional(),
    // Bridge-aware extras, all optional so a backend that predates the deBridge
    // failover still parses. `bridge` names the route, `bridgeFeeUsd` is its
    // cost as a decimal USD string, and `estimatedReceive` is what the quote
    // expects to land, in token units (6dp) rather than USD.
    bridge: bridgeNameSchema.optional(),
    bridgeFeeUsd: z.string().optional(),
    estimatedReceive: z.string().optional(),
    fees: transferFeesSchema.optional(),
});

// --- fund authorization challenge (HTTP 200 handshake answer from POST /speed/deposit|withdraw) ---

const evmAddressSchema = z.string().regex(/^0x[0-9a-fA-F]{40}$/u);
const decimalStringSchema = z.string().regex(/^[0-9]+$/u);
const bytes32Schema = z.string().regex(/^0x[0-9a-fA-F]{64}$/u);

/**
 * A withdrawal's bridge hop, described so the user's OWN wallet can pin it on
 * chain before any service wallet is allowed to move the funds.
 *
 * The backend quotes the bridge and answers with this descriptor; the user's
 * EOA calls `ServiceWalletRegistry.registerBridgeIntent` on `registryChainId`
 * with exactly these values (`src/lib/config/serviceWalletRegistry.ts`), and
 * RustSigner then refuses to sign the bridge deposit unless its calldata
 * matches the registered entry. `id` is the bridge request id the deposit
 * calldata carries, so registering it binds one specific hop, once.
 *
 * Everything here is checked against the user's own intent
 * (`assertBridgeIntentMatchesIntent`) before a wallet prompt opens.
 */
export const bridgeIntentDescriptorSchema = z.object({
    /**
     * Namespaces the intent store per bridge: 1 is Relay, 2 is deBridge, 3 is LI.FI. Only
     * ids this app knows are accepted, because an intent recorded under any
     * other namespace is one no signer reads (see `SUPPORTED_BRIDGE_IDS`).
     */
    bridgeId: z.literal(SUPPORTED_BRIDGE_IDS),
    id: bytes32Schema,
    registryChainId: z.number().int().positive(),
    registry: evmAddressSchema,
    originChainId: z.number().int().positive(),
    token: evmAddressSchema,
    /** Base units of `token` (USDC, 6dp). */
    amount: decimalStringSchema,
    destinationChainId: z.number().int().positive(),
    recipient: evmAddressSchema,
});

/**
 * The fund-moving operations RustSigner gates behind a fresh per-operation
 * user signature: the custodial family (`fund_operation_binding_fields` in
 * `RustSigner/src/v1.rs`) plus the HyperliquidManager movements that ARE the
 * HyperEVM deposit and withdrawal (`hl_fund_operation_fields`). Anything else
 * in a challenge is refused client-side.
 *
 * `hl_withdraw_unified_to_adapter` is the withdrawal sweep (the core USDC
 * lands on the user's OWN adapter).
 */
export const fundOperationNameSchema = z.enum([
    'approve_erc20',
    'withdraw_native',
    'withdraw_erc20',
    'withdraw_wrapped',
    'withdraw_unwrapped',
    'wrap_native',
    'unwrap_native',
    'upgrade_transfer',
    'upgrade_transfer_with_sig',
    'hl_fund',
    'hl_withdraw_unified_to_adapter',
    // the V1 to V2 migration payout: `CustodialWallet.withdrawErc20WithSig` to
    // the user's Snappy Wallet on HyperEVM, the same fields and slot layout as
    // `withdraw_erc20` under its own operation hash (RustSigner admits it only
    // on chain 999 and only to the `to` the proof names).
    'migrate_erc20',
]);

/**
 * One fund-moving action the backend wants the user to authorize — the wire
 * mirror of `FundAuthorizationDescriptor` in
 * `MoonHub.API/access/ISignerAccess.ts`. Every value here is re-derived
 * independently by RustSigner from the actual sign request — the user's
 * signature only goes through if the backend executes exactly what it
 * described here, so a lying backend gains nothing beyond a failed request.
 *
 * `key` names the slot: the client echoes it as the key in the request's
 * `fundAuthorizations` map. `spender` (approve family) lands in the signed
 * FundOperation `to` slot. `wallet` is null only while the custodial wallet
 * is not deployed yet — a state this client refuses to sign in.
 */
export const fundAuthorizationDescriptorSchema = z.object({
    key: z.string().min(1).max(64),
    operation: fundOperationNameSchema,
    chainId: z.number().int().nonnegative(),
    owner: evmAddressSchema,
    wallet: evmAddressSchema.nullable(),
    caller: evmAddressSchema,
    token: evmAddressSchema,
    to: evmAddressSchema.nullable(),
    spender: evmAddressSchema.nullable(),
    amount: decimalStringSchema,
    // upper bound the server accepts for `operationExpiry - now` (the on-chain
    // signature expiry the client mints and signs)
    maxAuthorizationSeconds: z.number().int().positive(),
    // RustSigner's hard cap on the proof envelope (expiresAt - issuedAt)
    maxProofWindowSeconds: z.number().int().positive(),
    reason: z.enum(['missing', 'invalid', 'expired']),
});

/**
 * The full handshake answer (`fundAuthorizationRequiredResponse` in
 * `MoonHub.API/managers/SpeedTradingJobManager.ts`), delivered as a normal
 * HTTP 200 body instead of a job. Deposit signs at most an approval + a
 * withdraw + headroom for one more; anything larger is refused rather than
 * signed blindly.
 *
 * A withdrawal also carries `bridgeIntent`: the bridge hop the user's own
 * wallet must register on chain before the proof is minted. It is absent on
 * deposits, and absent on a backend that predates the registry.
 */
export const fundAuthorizationRequiredResponseSchema = z.object({
    status: z.literal('fundAuthorizationRequired'),
    code: z.literal('FUND_AUTHORIZATION_REQUIRED'),
    required: z.array(fundAuthorizationDescriptorSchema).min(1).max(3),
    bridgeIntent: bridgeIntentDescriptorSchema.optional(),
    // What the backend has just quoted for the bridge leg. This is the ONE
    // moment the real figure exists before the user commits: the challenge is
    // raised after the route is quoted and before any prompt opens, so the
    // withdraw form can spend it rather than the flat estimate. `catch` rather
    // than plain `.optional()` on purpose: a fee it cannot read must never fail
    // the whole challenge and block a withdrawal over a display number.
    fees: transferFeesSchema.optional().catch(undefined),
});

export const speedJobKindSchema = z.enum([
    'deposit',
    'withdraw',
    'agentSetup',
    // V1 to V2 migration (`POST /speed/migrate`): the custodial balance paid to
    // the user's Snappy Wallet on HyperEVM. A jobs list carrying one has to
    // parse, so the kind is listed even where nothing reads it.
    'migrate',
]);

export const speedJobStatusSchema = z.enum([
    'queued',
    'running',
    'actionRequired',
    'authorizationRequired',
    'bridging',
    'settled',
    'failed',
    'refunded',
]);

export const speedJobResponseSchema = z.object({
    jobId: z.string(),
    networkId: z.number(),
    kind: speedJobKindSchema,
    status: speedJobStatusSchema,
    step: z.string(),
    message: z.string(),
    progress: z.number(),
    transferId: z.number().nullable(),
    tradeId: z.number().nullable(),
    /**
     * Free-form per-job payload: every job kind puts a different shape here and
     * this client reads a handful of fields opportunistically, so it stays
     * `z.any()`.
     *
     * The one member that moves money is `bridgeIntent`
     * ({@link bridgeIntentDescriptorSchema}), present while a withdrawal sits
     * at `status: 'actionRequired'` / `step: 'awaitingBridgeIntent'` because
     * the bridge hop was re-routed and needs a fresh on-chain registration.
     * That one is validated on its own before anything is signed (see
     * `bridgeIntentFromJob` in `features/custodial/services/bridgeIntent.ts`)
     * rather than being trusted through this field.
     */
    response: z.any().nullable(),
    error: z.string().nullable(),
    code: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});

export const speedJobsResponseSchema = z.object({
    jobs: z.array(speedJobResponseSchema),
});

export const depositStartResponseSchema = speedJobResponseSchema;
export const withdrawStartResponseSchema = speedJobResponseSchema;
export const migrateStartResponseSchema = speedJobResponseSchema;

export const depositStatusResponseSchema = z.object({
    transferId: z.number(),
    status: transferStatusSchema,
    relayRequestId: z.string().nullable(),
    originChainId: z.number(),
    originCurrency: z.string().nullable(),
    destinationChainId: z.number(),
    amount: z.string(),
    destinationAddress: z.string().nullable(),
    destinationCurrency: z.string().nullable(),
    txHashes: z.array(z.string()),
});

export const withdrawStartPayloadSchema = z.object({
    transferId: z.number(),
    status: transferStatusSchema,
    originChainId: z.number(),
    originCurrency: z.string(),
    amount: z.string(),
    destinationChainId: z.number(),
    destinationCurrency: z.string(),
    destinationType: z.enum(['custodial', 'eoa']),
    destinationAddress: z.string(),
    relayRequestId: z.string().optional(),
    coreWriterSendAssetTxHash: z.string().nullable(),
    txHashes: z.array(z.string()).optional(),
    // absent on a backend that predates the deBridge failover; see transferFeesSchema.
    fees: transferFeesSchema.optional(),
});

/**
 * `response` of a `migrate` job: the transfer row paid to the user's Snappy
 * Wallet on HyperEVM, plus what became of the small HYPE gas top-up the job
 * ends with. A gas top-up that could not be sent is a note, never a failed
 * job. Read with `safeParse`: the job's `response` stays `z.any()`, and a
 * field this client cannot read must never fail a migration that settled.
 */
export const migrateStartPayloadSchema = z.object({
    transferId: z.number().optional(),
    // the contract says `snappyWallet`; the withdraw payload passes the row's own
    // enum through (`Eoa`), so the row spelling is accepted too.
    destinationType: z.enum(['snappyWallet', 'SnappyWallet']),
    destinationAddress: z.string(),
    /** Base units of USDC (6dp). */
    amount: z.string(),
    txHashes: z.array(z.string()).optional().default([]),
    gas: z
        .object({
            sent: z.boolean(),
            txHash: z.string().nullable(),
            note: z.string().optional(),
        })
        .optional(),
});

/** A plain decimal USD string ('0.990000'), not base units. */
const usdDecimalSchema = z.string().regex(/^[0-9]+(\.[0-9]+)?$/u);

/**
 * `GET /speed/migrate/quote` (MoonHub.API, app JWT): what a migration started
 * now would move to the Snappy Wallet. `migratableUsd` is custodial + adapter
 * + the HyperCore balance above the `reserveUsd` that stays behind, with no
 * withdrawal floor and no either/or bucket rule (unlike `withdrawableUsdc`,
 * which can read 0 while a migration still has something to move). Every
 * figure is a plain decimal USD string with 6 decimals. An account with no
 * V1 wallet answers `hasWallet: false` and zeros.
 */
export const migrateQuoteResponseSchema = z.object({
    migratableUsd: usdDecimalSchema,
    custodialUsd: usdDecimalSchema,
    adapterUsd: usdDecimalSchema,
    coreUsd: usdDecimalSchema,
    reserveUsd: usdDecimalSchema,
    hasWallet: z.boolean(),
});

export const withdrawStatusResponseSchema = z.object({
    transferId: z.number(),
    status: transferStatusSchema,
    relayRequestId: z.string().nullable(),
    originChainId: z.number(),
    originCurrency: z.string().nullable(),
    amount: z.string(),
    destinationChainId: z.number(),
    destinationCurrency: z.string().nullable(),
    destinationType: z.enum(['Custodial', 'Eoa']).nullable(),
    destinationAddress: z.string().nullable(),
    txHashes: z.array(z.string()),
});

// --- GET /speed/balance ---

export const balanceResponseSchema = z.object({
    networkId: z.number(),
    // NO backend has ever sent this and nothing downstream reads it, but requiring it made the whole
    // balance fail to parse — numericTotal went null, `?? 0` rendered $0, and a funded wallet looked
    // empty. optional so a field nobody produces cannot zero the balance again.
    bootstrapComplete: z.boolean().optional(),
    custodialWallet: z.string(),
    adapter: z.string().nullable(),
    custodialUsdc: z.string(),
    adapterUsdc: z.string(),
    hyperCoreWithdrawable: z.string(),
    totalUsdc: z.string(),
    // what the withdraw saga can actually move (unified: hypercore-withdrawable + evm buckets via
    // the evm fallback; standard: evm-held buckets only). optional for rollout tolerance — older
    // backends omit it.
    withdrawableUsdc: z.string().optional(),
    // decimal USD strings (not base units). minWithdrawUsd is the floor that applies to the user's
    // NEXT withdrawal; flatFeeUsd is its estimated flat cost — '0.02' on both routes, because the
    // HyperCore route now sweeps to the user's own wallet and bridges out over HyperEVM exactly
    // like the EVM-only route, so both pay just the ~$0.02 relayer. optional for rollout
    // tolerance — older backends omit them.
    minWithdrawUsd: z.string().optional(),
    flatFeeUsd: z.string().optional(),
});

// --- GET /speed/balance/light ---

// Everything a balance needs that the CHAIN cannot report: the resolved addresses, the
// unified flag (mongo-only state — it decides which hyperliquid endpoint applies and
// whether core funds count as withdrawable), and the withdraw policy inputs.
//
// We read the USDC buckets ourselves via multicall + the public hyperliquid info API, so
// the backend does no RPC work for a balance refresh. Falls back to GET /speed/balance.
export const balanceLightResponseSchema = z.object({
    networkId: z.number(),
    configured: z.boolean(),
    custodialWallet: z.string(),
    adapter: z.string().nullable(),
    unified: z.boolean(),
    /** the USDC token to read balances of — never hardcode it here. */
    usdc: z.string(),
    // policy INPUTS, not decisions: the server keeps owning the thresholds, we do arithmetic.
    coreFloorUsd: z.number(),
    evmFloorUsd: z.number(),
    coreSweepFeeUsd: z.number(),
    evmFeeUsd: z.number(),
    // USDC every HyperCore exit sweep must leave behind on the user's own core account. It is
    // the reserve for HyperCore's one-time fee on the account's first transfer out ($1) plus the
    // ~$0.002 transfer gas per sweep — both come out of what stays on core, never out of the
    // amount swept. MoonHub.API keeps it out of the sweep it executes, so an advertised max that
    // does not subtract it is a number the user cannot actually withdraw. Optional with the
    // shared 1.01 default for rollout tolerance: this build ships ahead of the factions deploy.
    coreSweepGasBufferUsd: z.number().optional().default(1.01),
});

// --- GET /speed/trades ---

// display-only nullable fields use .nullish().default(null): the backend's serializer OMITS
// null-valued keys entirely (and agent-mode trades have qty backfilled after the fact), and a
// key zod merely marks .nullable() is still REQUIRED — one absent qty made the whole history
// parse throw and the terminal render "No trades yet" over a perfectly good response.
export const userTradeSchema = z.object({
    id: z.number(),
    tradeHash: z.string(),
    pairBase: z.string(),
    tokenIn: z.string(),
    isLong: z.boolean(),
    status: tradeStatusSchema,
    entryPrice: z.string().nullish().default(null),
    closePrice: z.string().nullish().default(null),
    qty: z.string().nullish().default(null),
    margin: z.string().nullish().default(null),
    originalAmountIn: z.string().nullable().optional(),
    takeProfit: z.string().nullable().optional(),
    stopLoss: z.string().nullable().optional(),
    // Native Hyperliquid trigger-order ids for the requested TP/SL legs. `null`
    // means the leg was requested but never installed (e.g. the ambiguous-transport
    // recovery path: the open timed out on the FE but Hyperliquid accepted it, so
    // the trigger orders were never placed) — the FE detects this and asks the
    // MoonHub install endpoint to (re)place them.
    tpOid: z.number().nullable().optional(),
    slOid: z.number().nullable().optional(),
    closeReason: tradeStatusSchema.nullable().optional(),
    // Present ONLY on limit-origin rows (a market open never carries it), so it
    // doubles as the "this row started as a limit order" marker everywhere.
    // The HL-rounded human-decimal price the entry was placed at.
    limitPrice: z.string().nullish().default(null),
    // the leverage the user chose at open, persisted by MoonHub.API; null on legacy rows
    leverage: z.number().nullish().default(null),
    // A partly filled limit entry: Hyperliquid only places the order's own TP/SL
    // once it FULLY fills, so MoonHub.API protects the filled part with cover legs.
    // limitCoverSz is the live size it last protected; limitEntryDone turns true
    // once the entry can no longer fill.
    limitCoverSz: z.string().nullish().default(null),
    limitEntryDone: z.boolean().nullish().default(null),
    pnl: z.string().nullish().default(null),
    openFee: z.string().optional(),
    executionFee: z.string().optional(),
    serviceFee: z.string().optional(),
    fundingFee: z.string().optional(),
    closeFee: z.string().optional(),
    holdingFee: z.string().optional(),
    settledAmount: z.string().nullable().optional(),
    openTxHash: z.string().nullish().default(null),
    closeTxHash: z.string().nullish().default(null),
    // serialized as an ISO string over HTTP
    createdAt: z.union([z.string(), z.date()]),
});

export const userTradesResponseSchema = z.object({
    success: z.literal(true),
    trades: z.array(userTradeSchema),
});

// --- GET /speed/live (public live tape) ---

/**
 * One trade on the public tape. Identity is the adapter and nothing else: it is
 * already visible on Hyperliquid, which is what lets a viewer verify a row
 * themselves, and it is the seed for the pseudonym rendered beside it.
 *
 * Deliberately absent: userId, wallet, and the trader's own address. This shape
 * is byte-identical to what the live socket pushes, so seeded rows and streamed
 * rows merge without a second code path.
 */
export const publicTapeTradeSchema = z.object({
    id: z.number(),
    adapterAddress: z.string(),
    coin: z.string().nullable(),
    dex: z.string().nullable(),
    isLong: z.boolean().nullable(),
    leverage: z.number().nullable(),
    status: tradeStatusSchema,
    entryPx: z.string().nullable(),
    closePx: z.string().nullable(),
    pnl: z.string().nullable(),
    /** the socket estimates pnl on a manual close; the seed never does */
    pnlEstimated: z.boolean().default(false),
    qty: z.string().nullable(),
    margin: z.string().nullable(),
    createdAt: z.union([z.string(), z.date()]).nullish().default(null),
});

export const liveTradesResponseSchema = z.object({
    success: z.literal(true),
    trades: z.array(publicTapeTradeSchema),
    hasMore: z.boolean().default(false),
    nextBefore: z.number().nullable().default(null),
});

// --- GET /speed/agent/status ---

/**
 * Per-user Hyperliquid agent-key state. `needsReactivation` is true when the
 * approval is missing, expired, or inside the backend's refresh margin, i.e. the
 * next open/close would otherwise stall ~60-120s on an inline re-approval. It is
 * a pure db read; it does NOT reflect an on-chain agent prune (e.g. after a full
 * withdrawal) — that surfaces as an `AGENT_REACTIVATION_REQUIRED` at order time.
 */
export const agentStatusResponseSchema = z.object({
    agentAddress: z.string().nullable(),
    validUntil: z.number().nullable(),
    needsReactivation: z.boolean(),
    // HIP-3 (additive — optional for rollout safety): `requiresHip3Activation`
    // is true when the account still needs the one-time unified-account
    // activation before its first HIP-3 (builder-market) open.
    hip3Enabled: z.boolean().optional(),
    requiresHip3Activation: z.boolean().optional(),
    // true when a builder fee is configured and this account has not approved that exact rate. it is
    // folded into needsReactivation server-side, so acting on needsReactivation alone is sufficient;
    // this is here so the prompt can be worded accurately and so the reason is visible in diagnostics.
    builderApprovalStale: z.boolean().optional(),
    // true when the user has no speed wallet at all — the same condition GET /speed/balance reports as
    // `custodialWalletMissing`. distinct from needsReactivation, which is ALSO true here: there is
    // nothing to reactivate yet, and POST /speed/agent/reactivate throws tradeSetupRequired for these
    // users, so a prompt driven off needsReactivation alone offers a button that cannot work.
    walletMissing: z.boolean().optional(),
    // true while the HyperCore bootstrap still has to run. that bootstrap deposits USDC out of the
    // custodial wallet, so agent setup fails with HIP3_SETUP_FUNDING_REQUIRED below 2 USDC — and it
    // fails inside the job, after reactivate already returned `queued`.
    bootstrapRequired: z.boolean().optional(),
});

// --- POST /speed/agent/reactivate ---

/**
 * Durable agent-setup job returned by proactive reactivation. A settled job's
 * public response includes the freshly approved agent address and expiry.
 */
export const agentReactivateResponseSchema = speedJobResponseSchema.extend({
    kind: z.literal('agentSetup'),
    response: z
        .object({
            agentAddress: z.string(),
            validUntil: z.number().nullable(),
        })
        .passthrough()
        .nullable(),
});

// --- POST /speed/agent/builder-fee ---

/** Result of the silent builder-code re-approval for a live agent. */
export const agentBuilderFeeResponseSchema = z.object({
    approved: z.boolean(),
    decibps: z.number().nullable(),
});

// --- POST /speed/agent/activate-hip3 ---

/**
 * Result of the one-time HIP-3 (unified-account) activation. Long-running (the
 * backend signs, posts and confirm-polls the abstraction switch). Only the
 * agent address is consumed; the response also carries the confirmed
 * abstraction state.
 */
export const agentActivateHip3ResponseSchema = z.object({
    agentAddress: z.string(),
});

// --- POST /speed/trade/ensure-triggers ---

/**
 * Result of installing any missing native TP/SL trigger orders for the user's
 * OPEN trades (`POST /speed/trade/ensure-triggers`). Used by the
 * ambiguous-transport recovery path: when an open timed out on the FE but
 * Hyperliquid accepted it and factions later flipped the trade to Open, the
 * requested trigger legs were never placed. Idempotent — only missing requested
 * legs are installed. `installed` reports the (possibly still-null) oids per
 * touched trade; `pending` counts trades whose size (`qty`) is not yet backfilled
 * and must be retried later.
 */
export const ensureTriggersResponseSchema = z.object({
    installed: z.array(
        z.object({
            tradeHash: z.string(),
            tpOid: z.number().nullable(),
            slOid: z.number().nullable(),
        }),
    ),
    pending: z.number(),
});

// --- POST /speed/trade/update-triggers ---

/**
 * Result of editing the TP/SL of an already-open trade
 * (`POST /speed/trade/update-triggers`). Unlike ensure-triggers this is
 * user-initiated and destructive: a leg sent as `null` has its resting trigger
 * CANCELLED, a leg sent as a price is (re)placed at that price, and an absent
 * leg is left alone. The echoed `takeProfit`/`stopLoss` are the FINAL prices
 * after the backend's Hyperliquid tick/size rounding — they are what the trade
 * row will carry once the trades query refetches, so they may differ from what
 * was typed. `tpOid`/`slOid` are the resulting native trigger-order ids (null
 * for a removed leg).
 */
export const updateTriggersResponseSchema = z.object({
    success: z.literal(true),
    coin: z.string(),
    takeProfit: z.string().nullable(),
    stopLoss: z.string().nullable(),
    tpOid: z.number().nullable(),
    slOid: z.number().nullable(),
});

// --- POST /speed/trade/update-margin ---

/**
 * Result of adding margin to an already-open isolated position
 * (`POST /speed/trade/update-margin`). `addedUsd` is the human USD amount the
 * backend moved from the account's free HyperCore margin into the position;
 * `margin` is the trade row's collateral after the top-up as a 6-dec base-unit
 * string (null on a legacy row that never recorded one). The live position's
 * own `marginUsed` / `liquidationPx` come from the position query, which the
 * caller refetches.
 */
export const updateMarginResponseSchema = z.object({
    success: z.literal(true),
    coin: z.string(),
    addedUsd: z.string(),
    margin: z.string().nullable(),
});

// --- GET /speed/wallet ---

export const walletResponseSchema = z.object({
    success: z.literal(true),
    wallet: z
        .object({
            userAddress: z.string(),
            custodialWallet: z.string(),
            adapterAddress: z.string().nullable(),
            networkId: z.number(),
        })
        .nullable(),
});

// --- GET /speed/liqmap/:coin ---

/** One slice of price where sampled positions get force-closed. */
export const liqClusterSchema = z.object({
    price: z.number(),
    low: z.number(),
    high: z.number(),
    notionalUsd: z.number(),
    wallets: z.number(),
    distancePct: z.number(),
});

export const liqMapSnapshotSchema = z.object({
    spot: z.number(),
    computedAt: z.number(),
    ageMs: z.number(),
    stale: z.boolean(),
    sample: z
        .object({
            addresses: z.number().optional(),
            positions: z.number().optional(),
            skipped: z.number().optional(),
        })
        .partial()
        .default({}),
    bucketPct: z.number().default(0.25),
    rangePct: z.number().default(15),
    /** clusters BELOW spot — longs that get liquidated if price falls there */
    longs: z.array(liqClusterSchema),
    /** clusters ABOVE spot — shorts that get squeezed if price rises there */
    shorts: z.array(liqClusterSchema),
    /** [pct, cumulative notional] for 1..rangePct, every position counted */
    cascade: z
        .object({
            down: z.array(z.tuple([z.number(), z.number()])),
            up: z.array(z.tuple([z.number(), z.number()])),
        })
        .default({ down: [], up: [] }),
});

export const liqMapResponseSchema = z.object({
    success: z.literal(true),
    networkId: z.number(),
    coin: z.string(),
    snapshot: liqMapSnapshotSchema.nullable(),
});

// --- GET speed/exposure (factions read facet) ---

/**
 * Money Hyperliquid is holding that NO trade row of ours accounts for.
 *
 * The incident it exists for: a 296-unit GTC short filled 22, the user closed
 * those 22, and the 274 remainder kept resting until it filled into a position
 * nothing tracked. The terminal showed no position while the balance was held,
 * and the orphan was liquidated. The server answers with exactly what it cannot
 * match to a row, so the FE never classifies anything itself.
 *
 * `coin` is the exchange's own name ('PONS', 'xyz:BRENTOIL'). The USD members
 * are plain decimal strings, NOT 6-decimal base units like `margin` on a trade
 * row — do not run them through `formatUnits`.
 */
export const untrackedPositionSchema = z.object({
    coin: z.string(),
    dex: z.string().nullish().default(null),
    side: z.enum(['LONG', 'SHORT']),
    /** position size in asset units, decimal string */
    size: z.string(),
    entryPx: z.string().nullish().default(null),
    /** plain decimal USD */
    marginUsd: z.string(),
    /** plain decimal USD */
    notionalUsd: z.string(),
    liquidationPx: z.string().nullish().default(null),
    leverage: z.number().nullish().default(null),
});

/** A resting order (entry or trigger) with no row behind it. */
export const untrackedOrderSchema = z.object({
    coin: z.string(),
    dex: z.string().nullish().default(null),
    /** Hyperliquid order id — the only handle the cancel endpoint takes. */
    oid: z.number(),
    side: z.enum(['BUY', 'SELL']),
    size: z.string(),
    limitPx: z.string(),
    orderType: z.string(),
    isTrigger: z.boolean(),
    triggerPx: z.string().nullish().default(null),
    reduceOnly: z.boolean(),
    /** plain decimal USD */
    notionalUsd: z.string(),
});

export const untrackedExposureSchema = z.object({
    adapterAddress: z.string().nullish().default(null),
    checkedAt: z.string(),
    positions: z.array(untrackedPositionSchema).default([]),
    orders: z.array(untrackedOrderSchema).default([]),
    /** plain decimal USD the exchange is holding across both lists */
    heldUsd: z.string(),
});

/**
 * Always-200: a read that cannot reach the exchange answers
 * `{ success: false, error }` rather than a status the client would surface as
 * a broken page. Nothing is shown to the user in that case — an exposure panel
 * that cannot see the exchange has nothing honest to say.
 */
export const exposureResponseSchema = z.discriminatedUnion('success', [
    z.object({ success: z.literal(true), exposure: untrackedExposureSchema }),
    z.object({ success: z.literal(false), error: z.string() }),
]);

// --- POST speed/trade/orders/cancel ---

/** One untracked order dropped by its Hyperliquid oid. */
export const cancelUntrackedOrderResponseSchema = z.object({
    success: z.literal(true),
    coin: z.string(),
    oid: z.number(),
});
